import React, { useState } from "react";
import { ChevronDown, Mail, MessageSquare, Clock, ArrowRight, Check } from "lucide-react";
import { submitContactForm } from "../utils/api";
import {
  Button,
  Container,
  Section,
  Eyebrow,
  Display,
  Hairline,
  SEO,
} from "./ui";
import { canonicalFor } from "../routes";

const FAQ = [
  {
    q: "What is an NFC business card?",
    a: "A thin PVC card with a small embedded NFC chip. Hold it within an inch of any modern phone and your WhoBrew page opens in the browser. No app on either side. Every card also has a printed QR code as a fallback for older phones.",
  },
  {
    q: "How do I update my card?",
    a: "Sign in to whobrew.us and edit your card. Changes are live the second you save. The same URL keeps pointing to your card forever, so previously shared links update automatically.",
  },
  {
    q: "Does it work on every phone?",
    a: "NFC works on iPhone 7 and newer and on every Android from the last eight years. The printed QR code on the back covers everything older.",
  },
  {
    q: "What is the return policy?",
    a: "Thirty-day money-back guarantee. Email support@whobrew.us if anything is off and we will refund the order in full.",
  },
  {
    q: "Do you offer team plans?",
    a: "Yes. WhoBrew for Teams gives a group of people centralized billing, branding control, and performance dashboards. Volume pricing for ten or more seats.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("General inquiry");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [openFAQ, setOpenFAQ] = useState<number | null>(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitContactForm({ name, email, subject, message });
    } catch {
      /* email may have sent regardless */
    }
    setSending(false);
    setSubmitted(true);
    setName("");
    setEmail("");
    setSubject("General inquiry");
    setMessage("");
  };

  return (
    <>
      <SEO
        title="Contact — WhoBrew"
        description="Reach the WhoBrew team. Sales, support, partnerships, or general questions. Most replies within 24 hours."
        canonical={canonicalFor("/contact")}
        jsonLd={FAQ_JSONLD}
      />

      {/* Hero */}
      <Section tone="page" spacing="loose" hairlineBottom>
        <Container width="prose">
          <Eyebrow>Contact</Eyebrow>
          <Display as="h1" scale="lg" italicWord="touch" className="mt-5">
            Get in touch.
          </Display>
          <p className="mt-7 text-lg text-coffee-700 dark:text-cream-200 dark:text-cream-200 leading-relaxed">
            Sales, support, partnerships, or a question we haven't answered yet. A real person on
            our team reads every message and replies within one business day.
          </p>
        </Container>
      </Section>

      {/* Form + sidebar */}
      <Section tone="page" spacing="default" hairlineBottom>
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Form */}
            <div className="lg:col-span-7">
              <Eyebrow>Send a message</Eyebrow>
              <Display as="h2" scale="md" className="mt-3 mb-8">
                Write to us directly.
              </Display>

              {submitted ? (
                <div className="border border-latte-300 dark:border-latte-800 dark:border-latte-800 rounded-lg p-10 bg-cream-100 dark:bg-coffee-800 dark:bg-coffee-800">
                  <Check className="w-8 h-8 text-success-700 mb-4" strokeWidth={2.5} />
                  <Display as="h3" scale="sm">Sent.</Display>
                  <p className="mt-4 text-body text-coffee-700 dark:text-cream-200 dark:text-cream-200">
                    A real person on our team will reply within one business day.
                  </p>
                  <Button variant="ghost" onClick={() => setSubmitted(false)} className="mt-6">
                    Send another
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <Field label="Name" id="name">
                    <input
                      id="name" type="text" required value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="field-input"
                      placeholder="Your name"
                    />
                  </Field>
                  <Field label="Email" id="email">
                    <input
                      id="email" type="email" required value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="field-input"
                      placeholder="you@company.com"
                    />
                  </Field>
                  <Field label="Subject" id="subject">
                    <select
                      id="subject" value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="field-input"
                    >
                      <option>General inquiry</option>
                      <option>Order and shipping</option>
                      <option>Technical support</option>
                      <option>Team and enterprise orders</option>
                      <option>Partnership</option>
                      <option>Press</option>
                    </select>
                  </Field>
                  <Field label="Message" id="message">
                    <textarea
                      id="message" required rows={6} value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="field-input resize-vertical"
                      placeholder="How can we help?"
                    />
                  </Field>
                  <Button type="submit" size="lg" loading={sending}>
                    {sending ? "Sending" : "Send message"}
                  </Button>
                  <style>{`
                    .field-input {
                      width: 100%;
                      padding: 0.75rem 1rem;
                      background: transparent;
                      border: 1px solid #c9b89e;
                      border-radius: 4px;
                      color: inherit;
                      font-family: inherit;
                      font-size: 1rem;
                      outline: none;
                      transition: border-color 150ms;
                    }
                    .field-input:focus {
                      border-color: #1f1410;
                    }
                    .dark .field-input {
                      border-color: #443a30;
                    }
                    .dark .field-input:focus {
                      border-color: #fbf7f1;
                    }
                  `}</style>
                </form>
              )}
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-5 space-y-10">
              <div>
                <Eyebrow>Direct lines</Eyebrow>
                <Display as="h3" scale="sm" className="mt-3 mb-6">Reach the right person.</Display>
                <div className="space-y-5">
                  <ContactRow icon={<MessageSquare className="w-4 h-4" />} label="Sales" detail={
                    <a href="mailto:sales@whobrew.us" className="hover:text-coffee-900 dark:hover:text-cream-50 transition-colors">sales@whobrew.us</a>
                  } />
                  <ContactRow icon={<Mail className="w-4 h-4" />} label="Support" detail={
                    <a href="mailto:support@whobrew.us" className="hover:text-coffee-900 dark:hover:text-cream-50 transition-colors">support@whobrew.us</a>
                  } />
                  <ContactRow icon={<Clock className="w-4 h-4" />} label="Response" detail="Within 24 hours, business days" />
                </div>
              </div>

              <Hairline />

              <div>
                <Eyebrow>Common questions</Eyebrow>
                <Display as="h3" scale="sm" className="mt-3 mb-6">Quick answers.</Display>
                <div className="divide-y divide-latte-300 dark:divide-latte-800 border-y border-latte-300 dark:border-latte-800 dark:border-latte-800">
                  {FAQ.map((item, i) => {
                    const open = openFAQ === i;
                    return (
                      <div key={i}>
                        <button
                          onClick={() => setOpenFAQ(open ? null : i)}
                          aria-expanded={open}
                          className="w-full text-left py-4 flex items-start justify-between gap-4"
                        >
                          <span className="font-display text-title text-coffee-900 dark:text-cream-50 dark:text-cream-50 leading-snug">
                            {item.q}
                          </span>
                          <ChevronDown
                            className={`w-4 h-4 mt-1 text-coffee-700 dark:text-cream-200 dark:text-cream-200 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                          />
                        </button>
                        {open && (
                          <div className="pb-5 text-body-sm text-coffee-700 dark:text-cream-200 dark:text-cream-200 leading-relaxed">
                            {item.a}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </aside>
          </div>
        </Container>
      </Section>

      {/* CTA */}
      <Section tone="ink" spacing="default">
        <Container className="text-center">
          <Display as="h2" scale="lg" italicWord="minute" className="mx-auto max-w-3xl">
            Card up in a minute.
          </Display>
          <div className="mt-10 flex justify-center">
            <Button to="/my-card" size="lg" onInk trailingIcon={<ArrowRight className="w-4 h-4" />}>
              Set up your card
            </Button>
          </div>
        </Container>
      </Section>
    </>
  );
}

function Field({ label, id, children }: { label: string; id: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="block font-mono text-eyebrow uppercase text-coffee-600 dark:text-cream-200 dark:text-cream-200 mb-2">
        {label}
      </label>
      {children}
    </div>
  );
}

function ContactRow({ icon, label, detail }: { icon: React.ReactNode; label: string; detail: React.ReactNode }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded bg-cream-100 dark:bg-coffee-800 dark:bg-coffee-800 border border-latte-300 dark:border-latte-800 dark:border-latte-700 flex items-center justify-center shrink-0 text-coffee-900 dark:text-cream-50 dark:text-cream-50">
        {icon}
      </div>
      <div>
        <p className="font-mono text-eyebrow uppercase text-coffee-600 dark:text-cream-200 dark:text-cream-200 mb-1">{label}</p>
        <p className="text-body text-coffee-700 dark:text-cream-200 dark:text-cream-200">{detail}</p>
      </div>
    </div>
  );
}

export default Contact;
